'use client';

import { createClient } from '@/lib/supabase/client';

export type StaffRole = 'admin' | 'church_staff' | 'greeter' | 'usher' | 'pastor' | 'business_office';

export interface Member {
  id: string;
  name: string;
  phone: string;
  email: string;
  status: 'Active' | 'Inactive' | 'New';
  joinDate: string;
  lastVisit: string;
  notes: string;
  nfcTagId?: string;
  department?: string;
  pipelineStage: 'visitor' | 'membership_class' | 'full_member';
  attendanceDates: string[];
}

export interface Visitor {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  howHeard: string;
  firstTime: boolean;
  prayerRequest: string;
  interestedIn: string[];
  date: string;
  followedUp: boolean;
}

export interface Department {
  id: string;
  name: string;
  leader: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  return false;
}

function rowToMember(row: any): Member {
  const member: Member = {
    id: row.id,
    name: row.name,
    phone: row.phone || '',
    email: row.email || '',
    status: row.status as Member['status'],
    joinDate: row.join_date || '',
    lastVisit: row.last_visit || '',
    notes: row.notes || '',
    nfcTagId: row.nfc_tag_id || undefined,
    department: row.department || '',
    pipelineStage: row.pipeline_stage as Member['pipelineStage'],
    attendanceDates: row.attendance_dates || [],
  };

  // Auto-mark as Inactive if last visit is more than 21 days ago
  if (member.status === 'Active' && member.lastVisit) {
    const lastVisitDate = new Date(member.lastVisit);
    const today = new Date();
    const diffDays = Math.floor((today.getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 21) {
      member.status = 'Inactive';
    }
  }

  return member;
}

function rowToVisitor(row: any): Visitor {
  return {
    id: row.id,
    name: row.name,
    phone: row.phone || '',
    email: row.email || '',
    address: row.address || '',
    howHeard: row.how_heard || '',
    firstTime: row.first_time ?? true,
    prayerRequest: row.prayer_request || '',
    interestedIn: row.interested_in || [],
    date: row.visit_date || '',
    followedUp: row.followed_up ?? false,
  };
}

export const memberService = {
  async getAll(): Promise<Member[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .order('name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(rowToMember) || [];
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return [];
    }
  },

  async create(member: Omit<Member, 'id'>): Promise<Member | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('members')
        .insert({
          name: member.name,
          phone: member.phone,
          email: member.email,
          status: member.status,
          join_date: member.joinDate,
          last_visit: member.lastVisit,
          notes: member.notes,
          nfc_tag_id: member.nfcTagId || null,
          department: member.department || '',
          pipeline_stage: member.pipelineStage,
          attendance_dates: member.attendanceDates,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? rowToMember(data) : null;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return null;
    }
  },

  async update(id: string, updates: Partial<Member>): Promise<Member | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, any> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.joinDate !== undefined) dbUpdates.join_date = updates.joinDate;
    if (updates.lastVisit !== undefined) dbUpdates.last_visit = updates.lastVisit;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    if (updates.nfcTagId !== undefined) dbUpdates.nfc_tag_id = updates.nfcTagId || null;
    if (updates.department !== undefined) dbUpdates.department = updates.department;
    if (updates.pipelineStage !== undefined) dbUpdates.pipeline_stage = updates.pipelineStage;
    if (updates.attendanceDates !== undefined) dbUpdates.attendance_dates = updates.attendanceDates;

    try {
      const { data, error } = await supabase
        .from('members')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? rowToMember(data) : null;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('members').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return false;
    }
  },

  async recordAttendance(memberId: string, memberName: string, recordedBy: string): Promise<boolean> {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    try {
      const { error: attError } = await supabase
        .from('attendance_records')
        .insert({ member_id: memberId, member_name: memberName, record_date: today, recorded_by: recordedBy });

      if (attError) {
        if (attError.code === '23505') return false; // already checked in today
        if (isSchemaError(attError)) throw attError;
        return false;
      }

      // Update member last_visit and attendance_dates
      const { data: memberData } = await supabase
        .from('members')
        .select('attendance_dates')
        .eq('id', memberId)
        .single();

      const existingDates: string[] = memberData?.attendance_dates || [];
      const updatedDates = [today, ...existingDates.filter((d: string) => d !== today)];

      await supabase
        .from('members')
        .update({ last_visit: today, attendance_dates: updatedDates })
        .eq('id', memberId);

      return true;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return false;
    }
  },

  subscribeToChanges(callback: (members: Member[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('members_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'members' }, async () => {
        const members = await memberService.getAll();
        callback(members);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};

export const visitorService = {
  async getAll(): Promise<Visitor[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('visitors')
        .select('*')
        .order('visit_date', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(rowToVisitor) || [];
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return [];
    }
  },

  async create(visitor: Omit<Visitor, 'id'>): Promise<Visitor | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('visitors')
        .insert({
          name: visitor.name,
          phone: visitor.phone,
          email: visitor.email,
          address: visitor.address,
          how_heard: visitor.howHeard,
          first_time: visitor.firstTime,
          prayer_request: visitor.prayerRequest,
          interested_in: visitor.interestedIn,
          visit_date: visitor.date,
          followed_up: visitor.followedUp,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? rowToVisitor(data) : null;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return null;
    }
  },

  async update(id: string, updates: Partial<Visitor>): Promise<Visitor | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, any> = {};
    if (updates.followedUp !== undefined) dbUpdates.followed_up = updates.followedUp;
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.email !== undefined) dbUpdates.email = updates.email;

    try {
      const { data, error } = await supabase
        .from('visitors')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? rowToVisitor(data) : null;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('visitors').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return false;
    }
  },

  subscribeToChanges(callback: (visitors: Visitor[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('visitors_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'visitors' }, async () => {
        const visitors = await visitorService.getAll();
        callback(visitors);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};

export const departmentService = {
  async getAll(): Promise<Department[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .order('name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(row => ({ id: row.id, name: row.name, leader: row.leader })) || [];
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      return [];
    }
  },
};

export const ROLE_LABELS: Record<StaffRole, string> = {
  admin: 'Administrator',
  church_staff: 'Church Staff',
  greeter: 'Greeter',
  usher: 'Usher',
  pastor: 'Pastor',
  business_office: 'Business Office',
};

export const canFullAccess = (role: StaffRole) => ['admin', 'church_staff'].includes(role);
export const canViewReports = (role: StaffRole) => ['admin', 'church_staff', 'pastor', 'business_office'].includes(role);
export const canRecordAttendance = (_role: StaffRole) => true;
export const canManageMembers = (role: StaffRole) => ['admin', 'church_staff', 'pastor'].includes(role);
export const canAddMembers = (role: StaffRole) => ['admin', 'church_staff', 'pastor', 'greeter', 'usher'].includes(role);
export const canAddVisitors = (role: StaffRole) => ['admin', 'church_staff', 'pastor', 'greeter', 'usher'].includes(role);
